import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, MessageCircle } from "lucide-react";
import WelcomeModal from "@/components/WelcomeModal";
import AuthLayout, { authStyles } from "@/components/AuthLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Cloudflare Turnstile. Unset = the widget never renders and signup behaves exactly as
// before, so this can ship ahead of the keys.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const Signup = ({ onLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    terms_accepted: false,
    job_alerts_opt_in: false,
    // Honeypot — hidden from real users, so anything here came from a bot.
    website: "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [newUserId, setNewUserId] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Load and render Turnstile only when a site key is configured.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let widgetId;
    const render = () => {
      if (!window.turnstile || !turnstileRef.current || widgetId !== undefined) return;
      widgetId = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };
    if (window.turnstile) {
      render();
      return;
    }
    const existing = document.querySelector('script[data-turnstile]');
    const script = existing || document.createElement("script");
    if (!existing) {
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.setAttribute("data-turnstile", "true");
      document.head.appendChild(script);
    }
    script.addEventListener("load", render);
    return () => {
      script.removeEventListener("load", render);
      if (widgetId !== undefined && window.turnstile) {
        try { window.turnstile.remove(widgetId); } catch { /* widget already gone */ }
      }
    };
  }, []);

  const handleGoogleSignup = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      const authUrl = response.data.auth_url;
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate Google signup:', error);
      toast.error('Failed to connect to Google. Please try again.');
    }
  };

  const handleAppleSignup = async () => {
    try {
      const response = await axios.get(`${API}/auth/apple/login`);
      const authUrl = response.data.auth_url;

      if (!authUrl) {
        toast.error('Apple Sign In is not available yet.');
        return;
      }

      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate Apple signup:', error);
      if (error.response?.status === 500 && error.response?.data?.detail?.includes('not configured')) {
        toast.error('Apple Sign In is not available yet.');
      } else {
        toast.error('Failed to connect to Apple. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error("First name and last name are required");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    if (!formData.terms_accepted) {
      toast.error("Please accept the Terms and Conditions");
      return;
    }

    setLoading(true);

    try {
      const submissionData = {
        email: formData.email.trim(),
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        full_name: `${formData.first_name.trim()} ${formData.last_name.trim()}`,
        role: "crew",
        status: "NEW",
        terms_accepted: formData.terms_accepted,
        job_alerts_opt_in: formData.job_alerts_opt_in,
        website: formData.website,
        turnstile_token: turnstileToken || undefined,
      };

      const response = await axios.post(`${API}/auth/signup`, submissionData);

      // Store user credentials in localStorage
      if (response.data.token && response.data.user) {
        // Merge form data with response to ensure first_name/last_name are included
        const userData = {
          ...response.data.user,
          first_name: response.data.user.first_name || formData.first_name.trim(),
          last_name: response.data.user.last_name || formData.last_name.trim(),
          email: response.data.user.email || formData.email.trim()
        };

        localStorage.setItem('shootzu_token', response.data.token);
        localStorage.setItem('shootzu_user', JSON.stringify(userData));

        // Call onLogin to update authentication state
        if (onLogin) {
          onLogin(response.data.token, userData, response.data.refresh_token || null);
        }
      }

      // Store user ID and show welcome modal
      setNewUserId(response.data.user?.id);
      setShowWelcomeModal(true);
      setLoading(false);

    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error.response?.data?.detail || "Signup failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <AuthLayout>
        {/* Heading */}
        <div className="auth-head" style={authStyles.heading}>Join the Zu Crew</div>
        <div className="auth-head" style={{ ...authStyles.sub, marginBottom: 20 }}>Create your free account — it takes a minute.</div>

        {/* Social buttons (side by side) */}
        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
          <button
            type="button"
            onClick={handleGoogleSignup}
            className="auth-social"
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 12, borderRadius: 12, background: "#fff", border: "none", color: "#1f2430", fontWeight: 600, fontSize: "13.5px", cursor: "pointer", fontFamily: "'Work Sans', sans-serif" }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 4.75 12 4.75z"/>
            </svg>
            Google
          </button>
          <button
            type="button"
            onClick={handleAppleSignup}
            className="auth-social"
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 12, borderRadius: 12, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.16)", color: "#fff", fontWeight: 600, fontSize: "13.5px", cursor: "pointer", fontFamily: "'Work Sans', sans-serif" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M17.05 12.6c-.03-2.7 2.2-4 2.3-4.06-1.25-1.84-3.2-2.09-3.9-2.12-1.66-.17-3.24.98-4.08.98-.84 0-2.14-.96-3.52-.93-1.81.03-3.48 1.05-4.41 2.67-1.88 3.27-.48 8.1 1.35 10.76.9 1.3 1.97 2.76 3.38 2.71 1.36-.05 1.87-.88 3.51-.88 1.64 0 2.1.88 3.53.85 1.46-.03 2.38-1.33 3.27-2.63 1.03-1.51 1.46-2.97 1.48-3.05-.03-.01-2.84-1.09-2.87-4.32M14.4 4.68c.74-.9 1.24-2.15 1.1-3.4-1.07.05-2.36.71-3.13 1.61-.68.79-1.29 2.06-1.13 3.27 1.2.09 2.42-.6 3.16-1.48"/></svg>
            Apple
          </button>
        </div>

        {/* divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
          <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
          <span style={{ fontSize: 11, color: "rgba(255,255,255,.4)" }}>or</span>
          <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Names */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <span style={authStyles.label}>First name</span>
              <input
                className="auth-input"
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                placeholder="John"
                style={{ ...authStyles.input, padding: "12px 14px" }}
                required
              />
            </div>
            <div>
              <span style={authStyles.label}>Last name</span>
              <input
                className="auth-input"
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                placeholder="Doe"
                style={{ ...authStyles.input, padding: "12px 14px" }}
                required
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <span style={authStyles.label}>Email</span>
            <input
              className="auth-input"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="you@email.com"
              style={{ ...authStyles.input, padding: "12px 14px" }}
              required
            />
          </div>

          {/* Password */}
          <div>
            <span style={authStyles.label}>Password</span>
            <div style={{ position: "relative" }}>
              <input
                className="auth-input"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder="At least 6 characters"
                style={{ ...authStyles.input, padding: "12px 14px", paddingRight: 44 }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(255,255,255,.45)", cursor: "pointer", display: "flex" }}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <span style={authStyles.label}>Confirm password</span>
            <input
              className="auth-input"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              placeholder="Re-enter password"
              style={{ ...authStyles.input, padding: "12px 14px" }}
              required
            />
          </div>

          {/* Terms */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: "12.5px", color: "rgba(255,255,255,.65)", cursor: "pointer", lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={formData.terms_accepted}
              onChange={(e) => handleChange('terms_accepted', e.target.checked)}
              style={{ width: 18, height: 18, accentColor: "#28C0D0", flex: "0 0 auto", marginTop: 1, cursor: "pointer" }}
              required
            />
            <span>
              I agree to the <Link to="/terms" className="auth-link">Terms</Link> and <Link to="/privacy" className="auth-link">Privacy Policy</Link>.
            </span>
          </label>

          {/* Job alerts opt-in — explicit, unticked by default (Spam Act consent) */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 9, fontSize: "12.5px", color: "rgba(255,255,255,.65)", cursor: "pointer", lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={formData.job_alerts_opt_in}
              onChange={(e) => handleChange('job_alerts_opt_in', e.target.checked)}
              style={{ width: 18, height: 18, accentColor: "#28C0D0", flex: "0 0 auto", marginTop: 1, cursor: "pointer" }}
            />
            <span>
              Email me when a new job matches my alerts. You can change this anytime in Settings.
            </span>
          </label>

          {/* Honeypot. Hidden from humans and assistive tech; bots fill it in. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website}
              onChange={(e) => handleChange("website", e.target.value)}
            />
          </div>

          {TURNSTILE_SITE_KEY && <div ref={turnstileRef} style={{ marginTop: 4 }} />}

          <button type="submit" disabled={loading} className="auth-primary" style={{ ...authStyles.primaryBtn, marginTop: 4, opacity: loading ? 0.7 : 1 }}>
            {loading ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-t-transparent" style={{ borderColor: "#0C1120", borderTopColor: "transparent" }}></span>
                Creating account…
              </span>
            ) : "Create account"}
          </button>
        </form>

        {/* Sign In Link */}
        <div style={{ textAlign: "center", fontSize: 13, color: "rgba(255,255,255,.55)", marginTop: 18 }}>
          Already have an account? <Link to="/login" className="auth-link" style={{ fontWeight: 600 }}>Sign in</Link>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 18, fontSize: 12, color: "rgba(255,255,255,.4)" }}>
          <button onClick={() => window.TurboChat?.toggle()} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "inherit", cursor: "pointer" }}>
            <MessageCircle className="w-3.5 h-3.5" />
            Support
          </button>
          <span>·</span>
          <span>© {new Date().getFullYear()} Shootzu</span>
        </div>
      </AuthLayout>

      {/* Welcome Modal */}
      <WelcomeModal
        show={showWelcomeModal}
        userName={`${formData.first_name} ${formData.last_name}`.trim()}
        onClose={() => {
          setShowWelcomeModal(false);
          navigate('/onboarding');
        }}
        userId={newUserId}
      />
    </>
  );
};

export default Signup;
